import * as THREE from 'three';

import { TriggerEffects, HoverEffects, DefaultSizing, InteractionValues } from './enums.js';

import { createMessageTextMesh, createBackgroundMesh, createButton } from './component360.js'
import { backgroundColor, textColor, font } from './styles360.js';

/** @type  {WeakMap<object, THREE.Group>} */
const _relativeHotspotsGroup = new WeakMap();

const DEFAULT_COLOR = 0x02528A; //fallback value for color just in case someone doesn't configure a hotspot correctly
const DEFAULT_OPACITY = .75; //fallback value for opacity just in case someone doesn't configure a hotspot correctly

/**
 * 
 * A class for any interaction that hotspots/interactions solely does with 360 
 */
export default class Hotspot360 {
	constructor(player) {
		this.player = player;

		//remove all the relative hotspots when we switch presentations
		this.player.on("loadnewpresentation", () => {
			_relativeHotspotsGroup.delete(this);
		});
	}

	/**
	 * Creates a circular hotspot in the world space based upon the properties of a hotspot
	 * @param {any} hotspot
	 */
	createAreaHotspot(hotspot) {
		const xr = this.player.xr();
		let material;

		const areaProperties = {
			color: hotspot.Color != null ? hotspot.Color : DEFAULT_COLOR,
			opacity: hotspot.Opacity > 0 ? hotspot.Opacity : DEFAULT_OPACITY,
			linewidth: 4,
			transparent: true,
			depthWrite: false,
			depthTest: false
		};

		if (hotspot.HasNoFill) {
			material = new THREE.LineBasicMaterial(areaProperties);
		} else {
			material = new THREE.MeshBasicMaterial(areaProperties);
		}

		let hotspotMesh;

		if (hotspot.Shape === "Rectangle") {
			hotspotMesh = this.#createRectangleHotspotMesh(hotspot, material);
		} else {
			hotspotMesh = this.#createCircleHotspotMesh(hotspot, material);
			hotspotMesh.lookAt(xr.camera.position);
		}

		//The raycast uses the name to reference back to the id that the hotspots have in hotspots.js
		hotspotMesh.name = hotspot.TimedId;
		hotspotMesh.triggerDelay = InteractionValues.TriggerDelay;
		hotspotMesh.triggerEffect = TriggerEffects.Hotspot;
		hotspotMesh.triggerParam = hotspot.TimedId;

		hotspotMesh.visible = false;
		xr.scene.add(hotspotMesh);

		return hotspotMesh;
	}

	#createCircleHotspotMesh(hotspot, material) {
		//math to get the coordinates of the middle of the hotspot
		const yOffset = hotspot.Y + (hotspot.Height / 2);
		const xOffset = hotspot.X + (hotspot.Width / 2);

		const position = this.#getPointIn3DSpace(xOffset, yOffset);

		//we have two different approaching to how to render our hotspots, either circles or spheres
		//if spheres, due to them being 3D shapes, we can render their origin directly on the sphere and they will render partically within the sphere
		//if circles, we then need to render them fully within the outer sphere, so we need to have the point we're rendering be closer to the center
		//depending on how big the hotspot is
		const calcRadius = (InteractionValues.VideoSphereRadius - (InteractionValues.VideoSphereRadius * hotspot.Width));
		const zPoint = position.z * calcRadius;
		const xPoint = position.x * calcRadius;
		const yPoint = position.y * calcRadius;

		const geometry = new THREE.CircleGeometry(InteractionValues.VideoSphereRadius * hotspot.Height * (Math.PI / 2), 128);

		let mesh;
		if (hotspot.HasNoFill) {
			//render the other one like normal, but have it have a opacity of 0
			mesh = new THREE.Mesh(geometry, new THREE.MeshBasicMaterial({
				opacity: 0,
				transparent: true,
				depthWrite: false,
				depthTest: false
			}));

			//then create a line out of the same geometry and set it as a child of normal one
			//first, we need to remove the first vertex, which is the center point of the circle
			//we will then have just the outer line of the circle to create a line out of
			const circleGeometry = geometry.clone();
			const itemSize = 3;
			circleGeometry.setAttribute('position',
				new THREE.BufferAttribute(
					circleGeometry.attributes.position.array.slice(itemSize,
						circleGeometry.attributes.position.array.length - itemSize
					), itemSize
				)
			);
			circleGeometry.index = null;
			const outline = new THREE.LineLoop(circleGeometry, material);
			mesh.add(outline);
		} else {
			mesh = new THREE.Mesh(geometry, material);
		}

		//why is x negative? How the video is wrapped on the sphere is that the ends of the video meet on the x-axis.
		//This means that the center of the presentation that we start looking at is directly opposite that
		mesh.position.set(-xPoint, yPoint, zPoint);
		return mesh;
	}

	#createRectangleHotspotMesh(hotspot, material) {
		//math to get the corners of the rectangle
		const leftX = hotspot.X;
		const rightX = hotspot.X + hotspot.Width;
		const topY = hotspot.Y;
		const bottomY = hotspot.Y + hotspot.Height;

		const topLeft = this.#getPointIn3DSpace(leftX, topY);
		const topRight = this.#getPointIn3DSpace(rightX, topY);
		const bottomLeft = this.#getPointIn3DSpace(leftX, bottomY);
		const bottomRight = this.#getPointIn3DSpace(rightX, bottomY);

		//construct a geometry using the points found for the corners
		const geometry = new THREE.BufferGeometry();
		const offset = InteractionValues.VideoSphereRadius;
		//why is x negative? How the video is wrapped on the sphere is that the ends of the video meet on the x-axis.
		//This means that the center of the presentation that we start looking at is directly opposite that
		const vertices = new Float32Array([
			-bottomLeft.x * offset, bottomLeft.y * offset, bottomLeft.z * offset,
			-bottomRight.x * offset, bottomRight.y * offset, bottomRight.z * offset,
			-topRight.x * offset, topRight.y * offset, topRight.z * offset,
			-topLeft.x * offset, topLeft.y * offset, topLeft.z * offset
		]);

		const indices = [
			0, 1, 2,
			2, 3, 0,
		];

		geometry.setIndex(indices);
		// itemSize = 3 because there are 3 values (components) per vertex
		geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));

		let mesh;
		if (hotspot.HasNoFill) {
			//render the other one like normal, but have it have a opacity of 0
			mesh = new THREE.Mesh(geometry, new THREE.MeshBasicMaterial({
				opacity: 0,
				transparent: true,
				depthWrite: false,
				depthTest: false
			}));
			//then create a line out of the same geometryand set it as a child of normal one
			const outline = new THREE.Line(geometry, material);
			mesh.add(outline);
		} else {
			mesh = new THREE.Mesh(geometry, material);
		}

		return mesh;
	}

	/**
	 * Creates a text element based upon the hotspot information passed into it and adds it to the scene.
	 * @param {any} hotspot
	 */
	createTextHotspot(hotspot) {
		const xr = this.player.xr();

		//math to get the coordinates of the middle of the hotspot
		const yOffset = hotspot.Y + (hotspot.Height / 2);
		const xOffset = hotspot.X + (hotspot.Width / 2);

		const position = this.#getClampedPosition(xOffset, yOffset);

		const calcRadius = DefaultSizing.Distance * 2;
		const zPoint = position.z * calcRadius;
		const xPoint = position.x * calcRadius;
		const yPoint = position.y * calcRadius;

		const hotspotTextSize = hotspot.TextSize;
		let textSize;

		if (hotspotTextSize === "Small") {
			textSize = .75;
		} else if (hotspotTextSize === "Medium") {
			textSize = 1.25;
		} else {
			textSize = 2;
		}

		const hotspotMesh = this.#createMessageWithLineBreaks(hotspot.DisplayText, .05, textSize, hotspot.Width * DefaultSizing.Scale / textSize, 0, hotspot.Color, hotspot.SecondaryColor);

		hotspotMesh.position.set(-xPoint, yPoint, zPoint);
		hotspotMesh.name = hotspot.TimedId;

		hotspotMesh.visible = false;
		hotspotMesh.lookAt(xr.camera.position);
		this.#addToViewpointRelativeHotspots(hotspotMesh);

		return hotspotMesh;
	}

	/**
	 * Converts the given piece of text into a mesh that line breaks when it reaches its max width
	 * @param {any} message
	 * @param {any} padding
	 * @param {any} height
	 * @param {any} width
	 * @param {any} opacity
	 * @param {any} msgColor
	 * @param {any} outlineColor
	 */
	#createMessageWithLineBreaks(message, padding = .25, height = DefaultSizing.Height, width = DefaultSizing.Height, opacity = 1, msgColor = textColor(), outlineColor = null) {
		const lines = this.#getLinesFromMessage(message, width);
		const totalLines = lines.length;
		const lineMeshes = [];

		lines.forEach(line => {
			const textMesh = createMessageTextMesh(line, height, msgColor, font(), outlineColor);
			lineMeshes.push(textMesh);
		});

		const longestTextWidth = this.#getLengthofWidestMesh(lineMeshes);
		const renderWidth = longestTextWidth + padding;
		const renderHeight = lineMeshes[0].geometry.parameters.height + padding;
		const backgroundMesh = createBackgroundMesh(renderWidth, renderHeight * totalLines, backgroundColor(), opacity);

		const lineYOffset = (totalLines - 1) / 2;

		let linesCreated = 0;
		lineMeshes.forEach(lineMesh => {
			backgroundMesh.add(lineMesh);
			lineMesh.position.set(0, (lineYOffset - linesCreated) * renderHeight, DefaultSizing.TextOffset);
			linesCreated++;
		});

		return backgroundMesh;
	}

	/**
	 * Breaks a message down into an array of string where each of them have a length less than the width passed in.
	 * @param {string} text
	 * @param {width} maxWidth
	 * @return {Array<string>} lines
	 */
	#getLinesFromMessage(text, maxWidth) {
		const canvas = document.createElement('canvas');
		const context = canvas.getContext('2d');
		const contextFont = `normal ${DefaultSizing.Scale}px ${font()}`;
		context.font = contextFont;

		const words = text.split(" ");
		const lines = [];
		let currentLine = words[0];

		for (let index = 1; index < words.length; index++) {
			const word = words[index];
			const width = context.measureText(currentLine + " " + word).width;
			if (width / DefaultSizing.Scale < maxWidth) {
				currentLine += " " + word;
			} else {
				lines.push(currentLine);
				currentLine = word;
			}
		}
		lines.push(currentLine);
		return lines;
	}

	/**
	 * Returns the longest width in a collection of meshes
	 * @param {Array<THREE.Mesh>} meshes
	 * @returns {number} width
	 */
	#getLengthofWidestMesh(meshes) {
		let longestWidth = 0;
		meshes.forEach(mesh => {
			const width = mesh.geometry.parameters.width;

			if (longestWidth < width) {
				longestWidth = width;
			}
		});

		return longestWidth;
	}

	/**
	 * Create a button that is added to the scene based upon the hotspot information passed into it.
	 * @param {any} hotspot
	 */
	createButtonHotspot(hotspot) {
		const xr = this.player.xr();

		//math to get the coordinates of the middle of the hotspot
		const yOffset = hotspot.Y + (hotspot.Height / 2);
		const xOffset = hotspot.X + (hotspot.Width / 2);

		const position = this.#getClampedPosition(xOffset, yOffset);

		const calcRadius = DefaultSizing.Distance * 2;
		const zPoint = position.z * calcRadius;
		const xPoint = position.x * calcRadius;
		const yPoint = position.y * calcRadius;

		const hotspotTextSize = hotspot.TextSize;
		let textSize;

		if (hotspotTextSize === "Small") {
			textSize = .75;
		} else if (hotspotTextSize === "Medium") {
			textSize = 1.25;
		} else {
			textSize = 2;
		}

		const hotspotMesh = createButton(hotspot.DisplayText, textSize, textSize, hotspot.Color, hotspot.SecondaryColor);

		hotspotMesh.position.set(-xPoint, yPoint, zPoint);
		hotspotMesh.name = hotspot.TimedId;
		hotspotMesh.triggerDelay = InteractionValues.TriggerDelay;
		hotspotMesh.triggerEffect = TriggerEffects.Hotspot;
		hotspotMesh.triggerParam = hotspot.TimedId;
		hotspotMesh.hoverEffect = HoverEffects.Scale;

		hotspotMesh.visible = false;
		hotspotMesh.lookAt(xr.camera.position);

		this.#addToViewpointRelativeHotspots(hotspotMesh);

		return hotspotMesh;
	}

	/**
	 * When provided offsets that range from 0 - 1, where 1 returns maxAngle / 2 and 0 return -maxAngle / 2
	 * and provided clamp angles, so that if offsets was 1 it would return clampAngle / 2 and 0 return -clampAngle / 2
	 * return a Vector3 in 3D space that matches those two new clamped angles
	 * @param {number} xOffset
	 * @param {number} yOffset
	 * @param {number} xClampAngle
	 * @param {number} yClampAngle
	 */
	#getClampedPosition(xOffset, yOffset, xClampAngle = 80, yClampAngle = 50) {
		const xMaxAngle = 180;
		const yMaxAngle = 90;

		const movedOffsetX = xOffset * (xClampAngle * 2) - xClampAngle;
		const translatedOffsetX = (movedOffsetX + xMaxAngle) / (xMaxAngle * 2);

		const movedOffsetY = yOffset * (yClampAngle * 2) - yClampAngle;
		const translatedOffsetY = (movedOffsetY + yMaxAngle) / (yMaxAngle * 2);

		return this.#getPointIn3DSpace(translatedOffsetX, translatedOffsetY);
	}

	#getPointIn3DSpace(x, y) {
		//Who's ready for some trig!!! Goal: to figure out where on the outer sphere (where the video is being rendered onto) we're rendering the hotspot

		/* okay, so we have the x offset, which is basically how far left/right into the presentation the hotspot is
		   well, the horizontal distance has now been wrapped into a circle and we know how far into the circle we are (the xOffset)
		   we need to convert this straight distance into a curved distance, i.e. radians
		   we also need to multiply by 2 since before we were working on a scale to 0 - 1 and 2π radians = 360 degree */
		const theta = x * 2 * Math.PI;

		//simarly, we need to get our vertical angle. We do the same math, but don't multiply by 2 since the vertical
		//range we're dealing with is only 0 to π radians, in other words, 0 - 180 degrees
		const phi = y * Math.PI;

		//we got our angles in radians! Now, we can do some caluculations to figure out the point in the space
		//the following math has been pulled from here: https://stackoverflow.com/a/969880/11466368
		//x = r * cos(phi) * sin(theta)
		//y = r * sin(phi) * sin(theta)
		//z = r * cos(theta)
		//if you wish to know the why behind the math, read this wikipedia section: https://en.wikipedia.org/wiki/Spherical_coordinate_system#Coordinate_system_conversions
		let zPoint = Math.sin(phi) * Math.cos(theta);
		let xPoint = Math.sin(phi) * Math.sin(theta);
		let yPoint = Math.cos(phi);

		return {
			x: xPoint,
			y: yPoint,
			z: zPoint
		}
	}

	//To get the buttons and text to follow the camera, we add them to a group that moves to where the camera is looking
	//rather than trying to move each hotspot when they appear on screen
	#addToViewpointRelativeHotspots(hotspot) {
		let hotspotGroup = _relativeHotspotsGroup.get(this);

		if (!hotspotGroup) {
			const xr = this.player.xr();
			hotspotGroup = new THREE.Group();
			hotspotGroup.position.set(0, 0, -1);
			xr.scene.add(hotspotGroup);
		}

		hotspotGroup.add(hotspot);
		_relativeHotspotsGroup.set(this, hotspotGroup);
	}

	/**
	 * Moves the holder of the hotspots that move with the camera to update its position and move to where the camera is looking 
	 * 
	*/
	updatePositionOfRelativeHotspots() {
		const normalizedVector = this.player.video360?.().getCameraDirectionNormalized();
		const hotspotGroup = _relativeHotspotsGroup.get(this);

		if (hotspotGroup) {
			hotspotGroup.position.set(normalizedVector.x, normalizedVector.y, normalizedVector.z);
			hotspotGroup.lookAt(0, 0, 0);
		}
	}
}